import { useEffect, useState } from "react";
import {
  Alert,
  DialogContent,
  Grid,
  Typography,
} from "@mui/material";
import CustomButton from "../../components/common/button/CustomButton";
import FormField from "../../components/common/FormField";
import Popup from "../../components/common/model/Popup";
import { InventoryPayloadItem } from "../../clientAdmin/components/storage/components/inventory/utils";

const initialVerificationFormData = {
  notes: "",
  driver_sign_delivery: "",
  status: "not-checked",
};

interface CrewInventoryNoteModalProps {
  open: boolean;
  item: InventoryPayloadItem | null;
  driverSignDelivery: string;
  onClose: () => void;
  onSave: (values: {
    notes: string;
    driver_sign_delivery: string;
    status: string;
  }) => void;
}

const CrewInventoryNoteModal = ({
  open,
  item,
  driverSignDelivery,
  onClose,
  onSave,
}: CrewInventoryNoteModalProps) => {
  const [formData, setFormData] = useState(initialVerificationFormData);
  const [notesError, setNotesError] = useState("");
  const [driverSignError, setDriverSignError] = useState("");

  useEffect(() => {
    const itemStatus = item?.status?.toLowerCase();
    setNotesError("");
    setDriverSignError("");
    setFormData({
      notes: item?.notes || "",
      driver_sign_delivery: driverSignDelivery || "",
      status:
        itemStatus === "verified" || itemStatus === "exception"
          ? itemStatus
          : "not-checked",
    });
  }, [item, open, driverSignDelivery]);

  const handleFieldChange = (event: any) => {
    const { id, name, value } = event.target;
    const field = (id || name) as keyof typeof initialVerificationFormData;
    setFormData((previous) => ({ ...previous, [field]: value }));
    if (field === "notes" && value.trim()) {
      setNotesError("");
    }
    if (field === "driver_sign_delivery" && value.trim()) {
      setDriverSignError("");
    }
  };

  const handleSubmit = () => {
    if (!formData.notes.trim()) {
      setNotesError("Notes are required.");
    }
    if (!formData.driver_sign_delivery.trim()) {
      setDriverSignError("Driver sign at delivery is required.");
    }
    if (!formData.notes.trim() || !formData.driver_sign_delivery.trim()) {
      return;
    }
    setNotesError("");
    setDriverSignError("");
    onSave(formData);
  };

  return (
    <Popup
      open={open}
      title="Item verification"
      onHide={onClose}
      fullWidth
      maxWidth="sm"
      popupBg="#fff"
      footer={
        <>
          <CustomButton variant="outlined" title="Cancel" onClick={onClose} />
          <CustomButton
            title="Save as draft"
            disabled={!item}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <DialogContent sx={{ padding: "16px !important" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              This item will be saved with draft status, along with its notes
              and the delivery signature.
            </Alert>
          </Grid>
          {item && (
            <Grid item xs={12}>
              <Grid
                container
                spacing={2}
                sx={{ p: 1.5, borderRadius: 1, backgroundColor: "#f5f8fa" }}
              >
                <Grid item xs={12} sm={8}>
                  <Typography variant="body2">
                    <strong>Item:</strong> {item.title}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    <strong>Qty:</strong> {item.quantity}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          <FormField
            id="notes"
            label="Notes"
            type="textarea"
            value={formData.notes}
            onChange={handleFieldChange}
            error={Boolean(notesError)}
            isRequired
            minRows={3}
            inSingleGridForm
            isNonMarginTop
          />
          {/* {notesError && (
            <Grid item xs={12}>
              <Typography color="error" variant="caption">
                {notesError}
              </Typography>
            </Grid>
          )} */}
          <FormField
            id="driver_sign_delivery"
            label="Driver sign at delivery"
            type="text"
            value={formData.driver_sign_delivery}
            onChange={handleFieldChange}
            error={Boolean(driverSignError)}
            // helperText={driverSignError}
            isRequired
            inSingleGridForm
            isNonMarginTop
            InputProps={{
              sx: {
                height: "36px",
              },
            }}
          />
          <FormField
            id="status"
            label="Status"
            type="select"
            value={formData.status}
            onChange={handleFieldChange}
            options={[
              { id: "not-checked", name: "Not checked" },
              { id: "verified", name: "Verified" },
              { id: "exception", name: "Exception" },
            ]}
            inSingleGridForm
            isNonMarginTop
          />
        </Grid>
      </DialogContent>
    </Popup>
  );
};

export default CrewInventoryNoteModal;
